// ** react imports
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// ** utils imports
import Transition from '../../../utils/Transition';

// ** enums imports
import { USER_ROLES } from '../../../enums/common';

function UserRoleSelect(props) {
  // props
  const { onChange, currentValue } = props;

  // vars - create options with proper labels
  const options = [
    {
      label: 'Admin & Manager',
      value: `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}`,
    },
    {
      label: 'Admin Only',
      value: USER_ROLES.ADMIN,
    },
    {
      label: 'Manager Only',
      value: USER_ROLES.MANAGER,
    },
  ];

  // states
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(() => {
    // Find the option that matches currentValue, default to first option
    return options.find(option => option.value === currentValue) || options[0];
  });

  // refs
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Sync selected state with currentValue prop
  useEffect(() => {
    const newSelected =
      options.find(option => option.value === currentValue) || options[0];
    setSelected(newSelected);
  }, [currentValue]);

  // hooks
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  // Handle option selection
  const handleOptionSelect = option => {
    setSelected(option);
    if (onChange) {
      onChange(option.value);
    }
    setDropdownOpen(false);
  };

  // Check if there's an active filter
  const hasActiveFilter =
    selected.value !== `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}`;

  return (
    <div className="relative z-20">
      <button
        ref={trigger}
        className={`btn justify-between min-w-44 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 relative ${
          hasActiveFilter
            ? 'border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20'
            : ''
        }`}
        aria-label="Select user role"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="flex items-center">
          {selected.value.includes(',') ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500"
              stroke="currentColor"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M2 22C2 17.5817 5.58172 14 10 14C14.4183 14 18 17.5817 18 22H16C16 18.6863 13.3137 16 10 16C6.68629 16 4 18.6863 4 22H2ZM10 13C6.685 13 4 10.315 4 7C4 3.685 6.685 1 10 1C13.315 1 16 3.685 16 7C16 10.315 13.315 13 10 13ZM10 11C12.21 11 14 9.21 14 7C14 4.79 12.21 3 10 3C7.79 3 6 4.79 6 7C6 9.21 7.79 11 10 11ZM18.2837 14.7028C21.0644 15.9561 23 18.752 23 22H21C21 19.564 19.5483 17.4671 17.4628 16.5271L18.2837 14.7028ZM17.5962 3.41321C19.5944 4.23703 21 6.20361 21 8.5C21 11.3702 18.8042 13.7252 16 13.9776V11.9646C17.6967 11.7222 19 10.264 19 8.5C19 7.11935 18.2016 5.92603 17.041 5.35635L17.5962 3.41321Z"></path>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500"
              viewBox="0 0 24 24"
              fill="currentColor"
              strokeWidth="1"
              stroke="currentColor"
            >
              <path d="M4 22C4 17.5817 7.58172 14 12 14C16.4183 14 20 17.5817 20 22H18C18 18.6863 15.3137 16 12 16C8.68629 16 6 18.6863 6 22H4ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11Z"></path>
            </svg>
          )}
          <span className="truncate">{selected.label}</span>
        </span>
        <svg
          className="shrink-0 ml-1 fill-current text-gray-400 dark:text-gray-500"
          width="8"
          height="5"
          viewBox="0 0 8 5"
        >
          <path d="M4 5L0 1h8L4 5z" />
        </svg>

        {/* Active filter indicator */}
        {hasActiveFilter && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-violet-500 rounded-full"></div>
        )}
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-10 absolute top-full left-0 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 py-1.5 rounded-lg shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div ref={dropdown}>
          {options.map((option, optionIndex) => (
            <button
              key={optionIndex}
              className={`flex items-center w-full hover:bg-gray-50 dark:hover:bg-gray-700/20 py-2 px-3 cursor-pointer text-left transition-colors ${
                selected.value === option.value
                  ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
              onClick={() => handleOptionSelect(option)}
            >
              <span className="text-sm font-medium">{option.label}</span>
              {selected.value === option.value && (
                <svg
                  className="w-4 h-4 ml-auto text-violet-600 dark:text-violet-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      </Transition>
    </div>
  );
}

// PropTypes for better type checking
UserRoleSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  currentValue: PropTypes.string,
};

export default UserRoleSelect;
